import {Box, Tabs, Tab} from '@mui/material';
import {t} from 'i18next';
import React, {
  ReactNode,
  SetStateAction,
  useState,
  Dispatch,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import TranscriptHeader from './transcript-header/TranscriptHeader';
import {
  TranscriptItemDataType,
  TranscriptPanelType,
  TranscriptSortColumn,
  TranscriptSortDirection,
  TranscriptSortKey,
  getTranscriptSortLabel,
} from '../AiInvestigateUtils';
import SearchAndFilterBar from './search-and-filter-bar/SearchAndFilterBar';
import AdvancedSearch, {
  AdvancedSearchFilters,
} from './advanced-search/AdvancedSearch';

import MyTranscripts from '../transcipts/my-transcripts/MyTranscripts';
import TeamTranscripts from '../transcipts/team-transcripts/TeamTranscripts';
import './TranscriptPanel.scss';
import ClearFilter from './clear-filter/ClearFilter';
import SearchFilterResults from './search-filter-results/SearchFilterResults';
import TranscriptPanelContext from './TranscriptPanelContext';
import {useGetTranscriptList} from '../../../../hooks/ai/investigate/useInvestigate';
import {
  IAIInvestigateTranscriptListItem,
  IAIInvestigateTranscriptListResponse,
  IAIInvestigateTranscriptPayload,
  ICreateConversationResponse,
} from '../../../../api/types/aiInvestigate.type';
import {formatToCustomUTC} from '../../../../utils/groupTranscriptsByDate';

const mapTranscript = (
  item: IAIInvestigateTranscriptListItem
): TranscriptItemDataType => ({
  transcriptId: item.transcriptId,
  transcriptName: item.transcriptName,
  description: item.description,
  createdDate: item.createdDate,
  updatedDate: item.updatedDate,
  ownerName: item.ownerName,
  isMyTranscript: item.isMyTranscript,
  indexID: item.indexID,
  indexName: item.indexName,
  isActiveIndex: true,
  formattedCreatedDate: formatToCustomUTC(item.createdDate),
  indexDocumentCount: item.indexDocumentCount,
});

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: Readonly<TabPanelProps>) {
  const {children, value, index, ...other} = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      className="tabpanel"
      {...other}
      data-testid={`transcript-panel-${index}`}
    >
      {value === index && (
        <div data-testid={`transcript-panel-children-${index}`}>{children}</div>
      )}
    </div>
  );
}

interface TranscriptProps {
  setIsCollapsed?: Dispatch<SetStateAction<boolean>>;
  selectedTranscriptId?: number;
  onTranscriptSelect?: (selectedTranscript: {
    transcriptId: number;
    isOwner: boolean;
    indexId: number;
    indexName: string;
    isActiveIndex: boolean;
    transcriptName: string;
    ownerName: string;
    isNew?: boolean;
    documentCount?: number;
  }) => void;
  onDisableNewChatChange: (value: boolean) => void;
  onNewChatClick: () => void;
  hasActiveIndexes: boolean;
  newTranscript?: TranscriptItemDataType | null;
  onTranscriptDeleted?: (id: number) => void;
  updatedConversation?: ICreateConversationResponse | null;
}
const ADJUSTMENT_HEIGHT = 100;
const TranscriptPanel: React.FC<TranscriptProps> = ({
  setIsCollapsed,
  selectedTranscriptId = 0,
  onTranscriptSelect,
  onDisableNewChatChange,
  onNewChatClick,
  hasActiveIndexes,
  newTranscript,
  onTranscriptDeleted,
  updatedConversation,
}) => {
  const [tabValue, setTabValue] = useState<number>(0);
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
  const [isFilterSearchApplied, setIsFilterSearchApplied] = useState(false);
  const [activeSearchQuery, setActiveSearchQuery] = useState('');
  const [appliedFilters, setAppliedFilters] = useState<AdvancedSearchFilters>({
    transcriptName: '',
    transcriptOwner: [],
    indexName: [],
    description: '',
  });
  const [pageNo, setPageNo] = useState(1);
  const [pageSize] = useState(20);
  const [transcripts, setTranscripts] = useState<TranscriptItemDataType[]>([]);
  const [draftFilters, setDraftFilters] =
    useState<AdvancedSearchFilters>(appliedFilters);
  const [_, setFilteredResults] = useState<TranscriptItemDataType[]>([]);
  const [checkFilterApplied, setCheckFilterApplied] = useState(false);
  const [loadedPages, setLoadedPages] = useState<Set<number>>(new Set());
  const firstRender = useRef(true);
  const hasAutoSelected = useRef(false);
  const isTriggerMounted = useRef(false);
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState(TranscriptPanelType.MyTranscripts);
  const [searchQuery, setSearchQuery] = useState('');

  const [selectedSortKey, setSelectedSortKey] = useState<TranscriptSortKey>(
    TranscriptSortKey.ModifiedNewest
  );

  const isSearchResultsView = !!activeSearchQuery || isFilterSearchApplied;
  const resultCount = useMemo(() => {
    return isSearchResultsView ? totalCount : transcripts.length;
  }, [isSearchResultsView, totalCount, transcripts.length]);
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    const tabType =
      newValue === 0
        ? TranscriptPanelType.MyTranscripts
        : TranscriptPanelType.TeamTranscripts;
    setPageNo(1);
    setLoadedPages(new Set());
    setTranscripts([]);
    setActiveTab(tabType);
  };
  const payload: IAIInvestigateTranscriptPayload | null = useMemo(() => {
    if (!activeTab) return null;

    const isModified = selectedSortKey.includes('modified');
    const isAsc = selectedSortKey.includes('oldest');
    const sortingColumn = isModified
      ? TranscriptSortColumn.LastUpdatedOn
      : TranscriptSortColumn.CreatedOn;
    const direction = isAsc
      ? TranscriptSortDirection.ASC
      : TranscriptSortDirection.DESC;

    return {
      simpleFilterText: activeSearchQuery || '',
      isMyTranscript: isSearchResultsView
        ? null
        : activeTab === TranscriptPanelType.MyTranscripts,
      sortingColumn,
      direction,
      displayLength: pageSize,
      pageNumber: pageNo,
      columnFilter: isFilterSearchApplied
        ? [
            {
              columnName: 'transcriptName',
              value: appliedFilters.transcriptName || '',
            },
            {
              columnName: 'description',
              value: appliedFilters.description || '',
            },
            {
              columnName: 'transcriptOwner',
              value: appliedFilters.transcriptOwner
                .map((o) => o.name)
                .join(','),
            },
            {
              columnName: 'indexName',
              value: appliedFilters.indexName.map((i) => i.name).join(','),
            },
          ]
        : [],
    };
  }, [
    activeTab,
    pageNo,
    pageSize,
    searchQuery,
    activeSearchQuery,
    isFilterSearchApplied,
    appliedFilters,
    selectedSortKey,
    isSearchResultsView,
  ]);
  const nextPagepayload: IAIInvestigateTranscriptPayload = useMemo(() => {
    return {
      ...payload,
      pageNumber: pageNo + 1,
    } as IAIInvestigateTranscriptPayload;
  }, [payload]);

  const {
    data: transcriptListResponse,
    isLoading,
    isFetching,
    isPlaceholderData,
  } = useGetTranscriptList(
    payload as IAIInvestigateTranscriptPayload,

    {
      enabled: !!payload,
      placeholderData: (prev: IAIInvestigateTranscriptListResponse) => prev,
    }
  );

  const {data: transcriptListNextPageResponse} = useGetTranscriptList(
    nextPagepayload as IAIInvestigateTranscriptPayload,
    {
      enabled: !!nextPagepayload,
      placeholderData: (prev: IAIInvestigateTranscriptListResponse) => prev,
    }
  );

  const handleSearchChange = (value: string) => {
    setSearchQuery?.(value);
    setActiveSearchQuery(value.trim());
    setPageNo(1);
    setLoadedPages(new Set());
    setTranscripts([]);
  };

  useEffect(() => {
    if (!newTranscript || isSearchResultsView) return;
    setTranscripts((prev) => [newTranscript, ...prev]);
  }, [newTranscript, isSearchResultsView]);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
  }, [payload]);

  useEffect(() => {
    if (!isFilterSearchApplied && activeSearchQuery === '') {
      setFilteredResults(transcripts);
    }
  }, [transcripts, isFilterSearchApplied, activeSearchQuery]);

  useEffect(() => {
    if (!isTriggerMounted.current) {
      isTriggerMounted.current = true;
      return;
    }
    if (!updatedConversation) return;
    if (
      activeTab === TranscriptPanelType.TeamTranscripts &&
      !activeSearchQuery?.trim().length &&
      !isFilterSearchApplied
    ) {
      return;
    }

    applyConversationUpdate(updatedConversation);
  }, [updatedConversation]);

  const applyConversationUpdate = (response: ICreateConversationResponse) => {
    const targetedTranscript = transcripts.find(
      (item) => item.transcriptId === response.transcriptId
    );
    if (!targetedTranscript) return;

    const updatedTranscript: TranscriptItemDataType = {
      ...targetedTranscript,
      updatedDate: response.questionDateTime,
    };

    if (selectedSortKey === TranscriptSortKey.ModifiedNewest) {
      setTranscripts((prev) => [
        updatedTranscript,
        ...prev.filter(
          (item) => item.transcriptId !== targetedTranscript.transcriptId
        ),
      ]);
    } else if (selectedSortKey === TranscriptSortKey.ModifiedOldest) {
      const isFullyLoaded = transcripts.length === totalCount;
      const backfillItem = transcriptListNextPageResponse?.transcriptItems?.[0];

      setTranscripts((prev) => {
        const othersWithoutTarget = prev.filter(
          (item) => item.transcriptId !== targetedTranscript.transcriptId
        );
        if (isFullyLoaded) {
          return [...othersWithoutTarget, updatedTranscript];
        }

        const isBackfillAlreadyPresent =
          backfillItem != null &&
          othersWithoutTarget.some(
            (item) => item.transcriptId === backfillItem.transcriptId
          );
        return backfillItem && !isBackfillAlreadyPresent
          ? [...othersWithoutTarget, mapTranscript(backfillItem)]
          : othersWithoutTarget;
      });
    }
  };

  const handleSortChange = (sortKey: TranscriptSortKey) => {
    setSelectedSortKey(sortKey);
    setPageNo(1);
    setLoadedPages(new Set());
    setTranscripts([]);
  };

  const handleFilterClick = () => {
    // Handle filter click
    setIsAdvancedSearchOpen(true);
    setDraftFilters(appliedFilters);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setActiveSearchQuery('');
    setSelectedSortKey(TranscriptSortKey.ModifiedNewest);
    setIsFilterSearchApplied(false);
    setCheckFilterApplied(false);
    setFilteredResults([]);
    setAppliedFilters({
      transcriptName: '',
      transcriptOwner: [],
      indexName: [],
      description: '',
    });
    setPageNo(1);
    setLoadedPages(new Set());
    setTranscripts([]);
  };

  const sortLabel = useMemo(
    () => getTranscriptSortLabel(selectedSortKey),
    [selectedSortKey]
  );

  const hasTranscriptData = (transcripts?.length ?? 0) > 0;
  const showClearFilterBar =
    (isSearchResultsView && (resultCount > 0 || isFilterSearchApplied)) ||
    (!isSearchResultsView && hasTranscriptData);

  const transcriptPanelContextValue = useMemo(
    () => ({selectedSortKey}),
    [selectedSortKey]
  );

  const handleCollapseClick = () => {
    setIsCollapsed?.((prev) => !prev);
  };

  const handleTranscriptSelect = useCallback(
    (selectedTranscript: {
      transcriptId: number;
      isOwner: boolean;
      indexId: number;
      indexName: string;
      isActiveIndex: boolean;
      transcriptName: string;
      ownerName: string;
      isNew?: boolean;
      documentCount?: number;
    }) => {
      onTranscriptSelect?.(selectedTranscript);
    },
    [onTranscriptSelect]
  );

  const handleApplyFilters = (filters: AdvancedSearchFilters) => {
    setAppliedFilters(filters);
    setIsAdvancedSearchOpen(false);
    setIsFilterSearchApplied(true);
    setCheckFilterApplied(true);
    setPageNo(1);
    setLoadedPages(new Set());
    setTranscripts([]);
  };

  const handleCancel = () => {
    setDraftFilters(appliedFilters);
    setIsAdvancedSearchOpen(false);
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const {scrollTop, scrollHeight, clientHeight} = e.currentTarget;
    const isBottom =
      scrollTop + clientHeight >= scrollHeight - ADJUSTMENT_HEIGHT;
    const totalPages = Math.ceil(totalCount / pageSize);

    if (isBottom && !isFetching && pageNo < totalPages) {
      setPageNo((prev) => prev + 1);
    }
  };

  const transcriptPanelTabs = [
    {
      id: TranscriptPanelType.MyTranscripts,
      label: t('ai.investigate.myTranscripts'),
      datatestid: 'lbl-my-transcripts',
      content: (
        <MyTranscripts
          transcripts={transcripts}
          setTranscripts={setTranscripts}
          onCreateClick={onNewChatClick}
          hasIndexes={hasActiveIndexes}
          selectedTranscriptId={selectedTranscriptId}
          onTranscriptSelect={handleTranscriptSelect}
          isFetching={isFetching}
          onScroll={handleScroll}
          isLoading={isLoading}
          onTranscriptDeleted={onTranscriptDeleted}
        />
      ),
    },
    {
      id: TranscriptPanelType.TeamTranscripts,
      label: t('ai.investigate.teamTranscripts'),
      datatestid: 'lbl-team-transcripts',
      content: (
        <TeamTranscripts
          transcripts={transcripts}
          selectedTranscriptId={selectedTranscriptId}
          onTranscriptSelect={handleTranscriptSelect}
          isFetching={isFetching}
          onScroll={handleScroll}
          isLoading={isLoading}
        />
      ),
    },
  ];

  const disableNewChat = !hasActiveIndexes;

  useEffect(() => {
    onDisableNewChatChange(disableNewChat);
  }, [disableNewChat]);

  useEffect(() => {
    if (!transcriptListResponse) return;
    if (loadedPages.has(pageNo)) return;

    const mapped: TranscriptItemDataType[] =
      transcriptListResponse.transcriptItems.map(mapTranscript);

    setTranscripts((prev) => {
      if (pageNo === 1) return mapped;
      const existingIds = new Set(prev.map((item) => item.transcriptId));
      const newData = mapped.filter(
        (item) => !existingIds.has(item.transcriptId)
      );
      return [...prev, ...newData];
    });
    setTotalCount(transcriptListResponse.totalCount || 0);
  }, [transcriptListResponse, pageNo, loadedPages]);

  // Auto-select the most recent (top) chat from My Chats on initial load.
  useEffect(() => {
    if (hasAutoSelected.current) return;
    if (activeTab !== TranscriptPanelType.MyTranscripts) return;
    if (isSearchResultsView) return;
    if (selectedTranscriptId !== 0) return;
    if (isFetching) return;
    if (isPlaceholderData) return;

    const firstItem = transcriptListResponse?.transcriptItems?.[0];
    if (!firstItem) return;

    const top = mapTranscript(firstItem);
    hasAutoSelected.current = true;
    handleTranscriptSelect({
      transcriptId: top.transcriptId,
      isOwner: top.isMyTranscript,
      indexId: top.indexID,
      indexName: top.indexName,
      isActiveIndex: top.isActiveIndex,
      transcriptName: top.transcriptName,
      ownerName: top.ownerName,
      documentCount: top.indexDocumentCount,
    });
  }, [
    transcriptListResponse,
    activeTab,
    isSearchResultsView,
    selectedTranscriptId,
    isFetching,
    isPlaceholderData,
    handleTranscriptSelect,
  ]);

  return (
    <TranscriptPanelContext.Provider value={transcriptPanelContextValue}>
      <Box
        className="transcript-panel-container"
        data-testid="transcript-panel"
      >
        <Box className="transcript-header">
          <TranscriptHeader
            onCollapseClick={handleCollapseClick}
            disableNewChat={disableNewChat}
            onNewChatClick={onNewChatClick}
          />
        </Box>
        {!isAdvancedSearchOpen && (
          <Box className="transcript-panel-search-filter-bar">
            <SearchAndFilterBar
              searchValue={searchQuery}
              onSearchChange={handleSearchChange}
              onSortChange={handleSortChange}
              onFilterClick={handleFilterClick}
              selectedSortKey={selectedSortKey}
              isFiltersApplied={checkFilterApplied}
              isInitialState={!hasTranscriptData}
            />
            {showClearFilterBar && (
              <ClearFilter
                resultCount={isSearchResultsView ? resultCount : 0}
                sortLabel={sortLabel}
                onClearFilters={handleClearFilters}
                showResultCount={isSearchResultsView}
                showClearFiltersLink={isFilterSearchApplied}
                showSortLabel={true}
              />
            )}
          </Box>
        )}

        {isAdvancedSearchOpen && (
          <AdvancedSearch
            filters={draftFilters}
            onChange={setDraftFilters}
            onCancel={handleCancel}
            onSearch={handleApplyFilters}
          />
        )}

        {!isAdvancedSearchOpen && (
          <>
            {!isSearchResultsView ? (
              <Box className="transcripts-tab">
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  className="tabs"
                >
                  {transcriptPanelTabs.map((tab) => (
                    <Tab
                      key={tab.id}
                      label={tab.label}
                      className="tab"
                      data-testid={tab.datatestid}
                    />
                  ))}
                </Tabs>

                {transcriptPanelTabs.map((tab, index) => (
                  <TabPanel
                    key={tab.id}
                    value={tabValue}
                    index={index}
                    data-testid={`${tab.datatestid}-content`}
                  >
                    {tab.content}
                  </TabPanel>
                ))}
              </Box>
            ) : (
              <SearchFilterResults
                transcripts={transcripts}
                setTranscripts={(updated) => {
                  setFilteredResults(updated);
                  setTranscripts(updated);
                }}
                selectedTranscriptId={selectedTranscriptId}
                onItemClick={(clickedItem) => {
                  onTranscriptSelect?.({
                    transcriptId: clickedItem.transcriptId,
                    isOwner: clickedItem.isMyTranscript,
                    indexId: clickedItem.indexID,
                    isActiveIndex: clickedItem.isActiveIndex,
                    indexName: clickedItem.indexName,
                    transcriptName: clickedItem.transcriptName,
                    ownerName: clickedItem.ownerName,
                    documentCount: clickedItem.indexDocumentCount,
                  });
                }}
                hasMore={transcripts.length < totalCount}
                isFetching={isLoading}
                onTranscriptDeleted={onTranscriptDeleted}
                onLoadMore={() => {
                  const totalPages = Math.ceil(totalCount / pageSize);

                  if (pageNo < totalPages) {
                    setPageNo((prev) => prev + 1);
                  }
                }}
              />
            )}
          </>
        )}
      </Box>
    </TranscriptPanelContext.Provider>
  );
};

export default TranscriptPanel;
